import { ButtonSecondary, RadioGroup, Toggle, type RadioGroupOption } from '@amelio/ui-library';
import { useId } from 'react';

import type {
    CompetencyVisibilityTiming,
    EmployeePortalCompetenciesUpdateRequest,
} from '@/types/performanceCompanyPreferences';

import {
    useCompanyPreferences,
    useUpdateEmployeePortalCompetencies,
} from '@/api/performance-company-preferences';
import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { showErrorToast, showSuccessToast } from '@/plugins/Toast';

const DATA_TEST_ID = 'employee-portal-competencies';

/**
 * The rule that separates this last section from the score levels above it, declared once so the
 * section keeps the same frame whether it is loading, failing or answering.
 */
const SECTION_FRAME = 'mt-12 border-t border-gray-200 pt-8';

/**
 * Aligns everything that depends on the toggle with the toggle's own label, so the question and its
 * answers read as one block. Derived from the control it follows rather than measured on screen:
 * the `smPlus` track is 44px and the row gap is 12px, so the label starts 56px in.
 *
 * Dropped on a phone, where those 56px would take a fifth of the column and break the label over
 * four lines: the block already reads as one when nothing else competes for the width.
 */
const TOGGLE_LABEL_INDENT = 'sm:pl-14';

/**
 * The two moments, in the order the question asks them, each with the key that carries its words.
 * Paired here so a moment cannot reach the screen without the sentence that explains it.
 */
const TIMINGS = [
    { value: 'OnSubmission', translationKey: 'onSubmission' },
    { value: 'AfterTheMeeting', translationKey: 'afterTheMeeting' },
] as const satisfies readonly { value: CompetencyVisibilityTiming; translationKey: string }[];

/**
 * What the employee portal shows of an employee's competencies, decided for the whole company:
 * whether an employee sees the levels a manager evaluation assessed them at, and from when. It sits
 * with the levels themselves because it is about those levels, and it is the only screen from which
 * the setting can be opened or closed.
 *
 * A company that has never saved a preference reads the server's own answer, which is closed: the
 * screen shows that, never a guess made while the answer is on its way.
 */
export const EmployeePortalCompetenciesSection = () => {
    const { t } = useAmelioTranslations();
    const { getManagerLabel } = useCompanyInformation();
    const { data, isLoading, isError, refetch } = useCompanyPreferences();
    const updatePreferences = useUpdateEmployeePortalCompetencies();
    const helpId = useId();
    const legendId = useId();

    if (isLoading) {
        return (
            <div className={SECTION_FRAME}>
                <p className={'text-base-400 py-8 text-center text-sm'}>
                    {t('competencies.employeePortalCompetencies.loading')}
                </p>
            </div>
        );
    }

    if (isError || !data) {
        return (
            <div className={SECTION_FRAME}>
                <div className={'flex flex-col items-center gap-4 py-8 text-center'}>
                    <p
                        role={'alert'}
                        className={'text-error text-sm'}
                    >
                        {t('competencies.employeePortalCompetencies.loadError')}
                    </p>

                    <ButtonSecondary
                        size={'md'}
                        fitContent
                        onClick={() => refetch()}
                        dataTestId={`${DATA_TEST_ID}-retry`}
                    >
                        {t('competencies.employeePortalCompetencies.retry')}
                    </ButtonSecondary>
                </div>
            </div>
        );
    }

    const { showCompetencyScoresToTheEmployee: areLevelsShown, employeeCompetencyVisibility } =
        data;

    // The server saves the two answers together, so each change carries the other one as it stands.
    const save = (request: EmployeePortalCompetenciesUpdateRequest) =>
        updatePreferences.mutate(request, {
            onSuccess: () =>
                showSuccessToast({
                    message: t('competencies.employeePortalCompetencies.saved'),
                }),
            onError: () =>
                showErrorToast({
                    message: t('competencies.employeePortalCompetencies.saveError'),
                }),
        });

    const timingOptions: RadioGroupOption[] = TIMINGS.map(({ value, translationKey }) => ({
        value,
        label: t(`competencies.employeePortalCompetencies.timing.${translationKey}`, {
            managerLabel: getManagerLabel(),
        }),
    }));

    return (
        <div
            className={SECTION_FRAME}
            data-testid={DATA_TEST_ID}
        >
            <div className={'mb-8'}>
                <h2 className={'text-navy text-subtitle mb-2 font-semibold'}>
                    {t('competencies.employeePortalCompetencies.title')}
                </h2>

                <p className={'text-navy/60 text-sm'}>
                    {t('competencies.employeePortalCompetencies.subtitle')}
                </p>
            </div>

            <div className={'flex flex-col gap-8'}>
                <div className={'flex flex-col gap-1.5'}>
                    <Toggle
                        checked={areLevelsShown}
                        onChange={(showCompetencyScoresToTheEmployee) =>
                            save({
                                showCompetencyScoresToTheEmployee,
                                employeeCompetencyVisibility,
                            })
                        }
                        disabled={updatePreferences.isPending}
                        size={'smPlus'}
                        rightLabel={t('competencies.employeePortalCompetencies.showLabel', {
                            managerLabel: getManagerLabel(),
                        })}
                        ariaDescribedBy={helpId}
                        dataTestId={`${DATA_TEST_ID}-toggle`}
                    />

                    <p
                        id={helpId}
                        className={`text-navy/60 text-sm ${TOGGLE_LABEL_INDENT}`}
                    >
                        {t('competencies.employeePortalCompetencies.showHelp')}
                    </p>
                </div>

                {/* Asked only once there is something to see: closed, the moment decides nothing. */}
                {areLevelsShown && (
                    <div className={`flex flex-col gap-3 ${TOGGLE_LABEL_INDENT}`}>
                        <h3
                            id={legendId}
                            className={'text-navy text-sm font-medium'}
                        >
                            {t('competencies.employeePortalCompetencies.timingLegend')}
                        </h3>

                        <RadioGroup
                            name={`${DATA_TEST_ID}-timing`}
                            size={'sm'}
                            options={timingOptions}
                            value={employeeCompetencyVisibility}
                            disabled={updatePreferences.isPending}
                            ariaLabelledBy={legendId}
                            onChange={(timing) =>
                                save({
                                    showCompetencyScoresToTheEmployee: areLevelsShown,
                                    employeeCompetencyVisibility:
                                        timing as CompetencyVisibilityTiming,
                                })
                            }
                            dataTestId={`${DATA_TEST_ID}-timing`}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
